import {
  DEFAULT_MESSAGE_CONFIG,
  DEFAULT_TRPC_MESSAGE_CONFIG,
  getApiErrorDescription,
  getTRPCErrorDescription,
  isApiError,
} from "@platform/shared/errors";
import { Icon, IconLoader } from "@platform/ui/components";
import * as React from "react";
import { Toaster as Sonner, toast as sonnerToast } from "sonner";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";
import { isTRPCClientError } from "~/shared/api/core/trpc.utils";

const TOAST_DURATION = 5000;

export function Toaster(props: React.ComponentProps<typeof Sonner>) {
  return (
    <Sonner
      position="bottom-right"
      theme="light"
      style={
        {
          "--width": "420px",
        } as React.CSSProperties
      }
      duration={TOAST_DURATION}
      {...props}
    />
  );
}

type RenderToastOptions = Omit<ToastProps, "id" | "title">;

function renderToast(title?: string, options?: RenderToastOptions) {
  return sonnerToast.custom((id) => <HeadlessToast id={id} title={title} {...options} />);
}

interface ToastProps {
  id: string | number;
  title?: string;
  description?: string;
  closeButton?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
  cancel?: {
    label: string;
    onClick: () => void;
  };
  icon?: React.ReactNode;
  variant?: "default" | "catastrophic";
}

export function HeadlessToast({
  id,
  title,
  description,
  action,
  cancel,
  icon,
  variant = "default",
  closeButton,
}: ToastProps) {
  const { root, titleSlot, descriptionSlot, buttonSlot, actionRoot } = toastStyle({ variant });

  return (
    <div className={root()} key={id}>
      {closeButton && (
        <button
          className={twMerge(
            "absolute top-0 left-0 -translate-1/2 p-1",
            "[--close-bttn-bg:#ffffff] [--close-bttn-border:#dde1eb] [--close-bttn-color:#101828] [--close-bttn-hover-bg:#f8f9fd]",
            "rounded-full border border-(--close-bttn-border) bg-(--close-bttn-bg) text-(--close-bttn-color) transition-colors hover:bg-[--close-bttn-hover-bg]",
          )}
          onClick={() => sonnerToast.dismiss(id)}
        >
          <Icon className="size-3" symbol="close-line" />
          <div className="sr-only">Close</div>
        </button>
      )}

      {icon && <div className="pt-4">{icon}</div>}

      <div className="flex w-full flex-1">
        <div className="grid w-full gap-0.5 py-4 pr-4">
          <div className={titleSlot()}>{title}</div>
          {description && <div className={descriptionSlot()}>{description}</div>}
        </div>

        {action || cancel ? (
          <div className={actionRoot()}>
            {action && (
              <button className={buttonSlot({ className: "font-medium" })} onClick={action.onClick}>
                {action.label}
              </button>
            )}
            {cancel && (
              <button
                onClick={() => {
                  cancel.onClick();
                  sonnerToast.dismiss(id);
                }}
                className={buttonSlot({ className: "border-ds-stroke-primary [button+&]:border-t" })}
              >
                {cancel.label}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const toastStyle = tv({
  slots: {
    root: "flex w-[420px] gap-4 border pl-4 overflow-clip bg-ds-surface-foundation shadow-lg border-ds-stroke-primary rounded-xl",
    titleSlot: "text-ds-text-primary text-pretty text-sm leading-normal font-medium",
    descriptionSlot: "text-ds-text-secondary text-pretty text-sm",
    actionRoot: "shrink-0 grid border-l border-l-ds-stroke-primary",
    buttonSlot:
      "text-sm text-ds-text-primary px-4 hover:bg-ds-surface-2 transition-colors dark:hover:bg-ds-surface-inverse! dark:hover:text-ds-text-inverse",
  },
  variants: {
    variant: {
      default: {
        root: [],
        titleSlot: [],
        descriptionSlot: [],
        button: [],
      },
      catastrophic: {
        root: "bg-ds-red-9 border-none",
        titleSlot: "text-ds-text-inverse dark:text-ds-text-primary",
        actionRoot: "dark:border-white/30",
        descriptionSlot: "text-ds-text-inverse dark:text-ds-text-primary",
        buttonSlot: "text-ds-text-inverse dark:text-ds-text-primary text-sm dark:border-white/30 hover:text-ds-red-10",
      },
    },
  },
});

const TOAST_ICONS = {
  success: <Icon className="text-ds-green-9 m-0" symbol="ri-checkbox-circle-fill" />,
  error: <Icon className="text-ds-red-9 m-0" symbol="ri-close-circle-fill" />,
  info: <Icon className="text-ds-text-primary m-0" symbol="information-fill" />,
  warning: <Icon className="text-ds-yellow-9 m-0" symbol="error-warning-fill" />,
  loading: (
    <div className="grid size-5 place-items-center">
      <IconLoader animate className="size-4" />
    </div>
  ),
} as const;

interface PromiseToastProps extends Omit<ToastProps, "title"> {
  promise: Promise<any> | (() => Promise<any>);
  loadingMessage: string | (() => string);
  successMessage: string | ((result: any) => string);
  errorMessage: string | ((error: any) => string);
  duration?: number;
}

function PromiseToast({
  promise,
  loadingMessage,
  successMessage,
  errorMessage,
  duration = TOAST_DURATION,
  ...rest
}: PromiseToastProps) {
  const [status, setStatus] = React.useState<"loading" | "success" | "error">("loading");
  const [result, setResult] = React.useState<any>(null);
  const [error, setError] = React.useState<any>(null);
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    const promiseToExecute = typeof promise === "function" ? promise() : promise;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    isMountedRef.current = true;

    promiseToExecute
      .then((data) => {
        if (!isMountedRef.current) return;
        setResult(data);
        setStatus("success");
      })
      .catch((err) => {
        if (!isMountedRef.current) return;
        setError(err);
        setStatus("error");
      })
      .finally(() => {
        if (!isMountedRef.current) return;
        // Auto-dismiss after duration when promise completes
        timeoutId = setTimeout(() => {
          sonnerToast.dismiss(rest.id);
        }, duration);
      });

    return () => {
      isMountedRef.current = false;
      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
    };
  }, [duration, promise, rest.id]);

  const getMessage = () => {
    switch (status) {
      case "loading":
        return typeof loadingMessage === "function" ? loadingMessage() : loadingMessage;
      case "success":
        return typeof successMessage === "function" ? successMessage(result) : successMessage;
      case "error":
        return typeof errorMessage === "function" ? errorMessage(error) : errorMessage;
    }
  };

  const icon = TOAST_ICONS[status];

  return <HeadlessToast {...rest} title={getMessage()} icon={icon} />;
}

const api = {
  error: ({
    error,
    title,
    descriptionConfig,
    trpcDescriptionConfig,
    options,
  }: {
    error: unknown;
    title: string;
    descriptionConfig?: Partial<typeof DEFAULT_MESSAGE_CONFIG>;
    trpcDescriptionConfig?: Partial<typeof DEFAULT_TRPC_MESSAGE_CONFIG>;
    options?: RenderToastOptions;
  }) => {
    if (isApiError(error)) {
      return renderToast(title, {
        ...options,
        icon: TOAST_ICONS.error,
        description: getApiErrorDescription(error, descriptionConfig),
      });
    }

    if (isTRPCClientError(error)) {
      return renderToast(title, {
        ...options,
        icon: TOAST_ICONS.error,
        description: getTRPCErrorDescription(error.data?.code, trpcDescriptionConfig),
      });
    }

    return renderToast(title, {
      ...options,
      icon: TOAST_ICONS.error,
    });
  },
};

export const toast = Object.assign(renderToast, {
  dismiss: sonnerToast.dismiss,
  catastrophic: (title?: string, options?: RenderToastOptions) =>
    renderToast(title, { variant: "catastrophic", ...options }),
  warning: (title?: string, options?: RenderToastOptions) =>
    renderToast(title, { variant: "default", icon: TOAST_ICONS.warning, ...options }),
  success: (title?: string, options?: RenderToastOptions) =>
    renderToast(title, { variant: "default", icon: TOAST_ICONS.success, ...options }),
  error: (title?: string, options?: RenderToastOptions) =>
    renderToast(title, { variant: "default", icon: TOAST_ICONS.error, ...options }),
  info: (title?: string, options?: RenderToastOptions) =>
    renderToast(title, { variant: "default", icon: TOAST_ICONS.info, ...options }),
  loading: (title?: string, options?: RenderToastOptions) =>
    renderToast(title, { variant: "default", icon: TOAST_ICONS.loading, ...options }),
  promise: <T = any, E = any>(
    promise: Promise<T> | (() => Promise<T>),
    messages: {
      loading: string | (() => string);
      success: string | ((result: T) => string);
      error: string | ((error: E) => string);
    },
    options?: Omit<ToastProps, "title"> & { duration?: number },
  ) =>
    sonnerToast.custom((id) => (
      <PromiseToast
        id={id}
        promise={promise}
        loadingMessage={messages.loading}
        successMessage={messages.success}
        errorMessage={messages.error}
        duration={options?.duration}
        {...options}
      />
    )),
  api,
});
